import React from 'react';
import { Formik } from 'formik';
import * as yup from 'yup';
import {
  Typography,
  Button,
  TextField,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Grid,
  MenuItem
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { makeStyles } from '@material-ui/core/styles';
import { states } from 'helpers/usStates';


const zipRegRxp = /(^\d{5}$)|(^\d{5}-\d{4}$)/;

const useStyles = makeStyles((theme) => ({
  closeButton: {
    position: 'absolute',
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: theme.palette.grey[500],
  },
  dialogContent: {
    width: '100%',
    height: '100%',
  }
}));

export default function BankingInformation({ affiliateData, isOpen, onClose, onConfirm }) {
  const classes = useStyles();

  return (
    <Dialog
      open={isOpen}
      fullWidth
      maxWidth="sm"
      onClose={onClose}
      aria-labelledby="confirm-dialog"
    >
      <DialogTitle id="confirm-dialog">
        <Typography variant="h3">Banking Information for Commission Payout</Typography>
        <IconButton aria-label="close" className={classes.closeButton} onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Grid item spacing={3}>
          <Formik
            initialValues={affiliateData}
            validationSchema={
              yup.object({
                businessName: yup
                  .string()
                  .required('Field is required'),
                streetAddress: yup
                  .string()
                  .required('Field is required'),
                city: yup
                  .string()
                  .required('Field is required'),
                state: yup
                  .string()
                  .required('Field is required'),
                zipCode: yup
                  .string()
                  .required('Field is required')
                  .matches(zipRegRxp, 'Please enter a valid zip code (Ex: 90602 or 90602-1234)'),
                emailAddress: yup
                  .string()
                  .email('Email is invalid')
                  .required('Field is required'),
                routingNumber: yup
                  .string()
                  .required('Field is required'),
                accountNumber: yup
                  .string()
                  .required('Field is required'),
                confirmAccountNumber: yup
                  .string()
                  .required('Field is required')
                  .oneOf([yup.ref('accountNumber')], 'Account number should be the same'),
              })
            }
            onSubmit={onConfirm}
          >
            {({
              values,
              errors,
              touched,
              handleBlur,
              handleChange,
              handleSubmit
            }) => (
              <form noValidate autoComplete="off" className="p-4" onSubmit={handleSubmit}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      required
                      className="w-full"
                      label="Business Name"
                      name="businessName"
                      variant="outlined"
                      value={values.businessName}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.businessName && Boolean(errors.businessName)}
                      helperText={touched.businessName && errors.businessName}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      required
                      className="w-full"
                      label="Street Address"
                      name="streetAddress"
                      variant="outlined"
                      value={values.streetAddress}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.streetAddress && Boolean(errors.streetAddress)}
                      helperText={touched.streetAddress && errors.streetAddress}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      required
                      className="w-full"
                      label="City"
                      name="city"
                      value={values.city}
                      variant="outlined"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.city && Boolean(errors.city)}
                      helperText={touched.city && errors.city}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      required
                      select
                      className="w-full"
                      variant="outlined"
                      label="State"
                      name="state"
                      value={values.state}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.state && Boolean(errors.state)}
                      helperText={touched.state && errors.state}
                    >
                      {states.map((state, index) => (
                        <MenuItem value={state.name} key={`state-${index}`}>{state.name}</MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      required
                      className="w-full"
                      label="ZIP"
                      name="zipCode"
                      variant="outlined"
                      value={values.zipCode}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.zipCode && Boolean(errors.zipCode)}
                      helperText={touched.zipCode && errors.zipCode}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      required
                      className="w-full"
                      label="Email Address"
                      variant="outlined"
                      name="emailAddress"
                      value={values.emailAddress}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.emailAddress && Boolean(errors.emailAddress)}
                      helperText={touched.emailAddress && errors.emailAddress}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      required
                      className="w-full"
                      label="Routing Number"
                      variant="outlined"
                      name="routingNumber"
                      value={values.routingNumber}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.routingNumber && Boolean(errors.routingNumber)}
                      helperText={touched.routingNumber && errors.routingNumber}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      required
                      className="w-full"
                      label="Account Number"
                      variant="outlined"
                      name="accountNumber"
                      value={values.accountNumber}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.accountNumber && Boolean(errors.accountNumber)}
                      helperText={touched.accountNumber && errors.accountNumber}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      required
                      className="w-full"
                      label="Confirm Account Number"
                      variant="outlined"
                      name="confirmAccountNumber"
                      value={values.confirmAccountNumber}
                      onChange={handleChange}
                      onPaste={(e) => {
                        e.preventDefault();
                      }}
                      onBlur={handleBlur}
                      error={touched.confirmAccountNumber && Boolean(errors.confirmAccountNumber)}
                      helperText={touched.confirmAccountNumber && errors.confirmAccountNumber}
                    />
                  </Grid>
                  <Grid item xs={4} >
                    <Button
                      className="mb-2"
                      type="submit"
                      color="primary"
                      variant="contained"
                    >
                      Save
                    </Button>
                  </Grid>
                </Grid>
              </form>
            )}
          </Formik>
        </Grid>
      </DialogContent>
    </Dialog>
  );
}
